// PRD §3.3 — Grade Cutoff section inside Item Detail. SUM mode only:
//   - Three Grade cards (G1/G2/G3) with chained ranges:
//       G1: 0 ~ g1Max (input)
//       G2: g1Max+1 (auto) ~ g2Max (input)
//       G3: g2Max+1 (auto) ~ "Max"
//   - Per-grade Guideline shown READ-ONLY (PRD 04 v1.5 §3.3 — linked 1:1 from
//     the Management Guideline screen / PRD 07; the editable dropdown was
//     removed). Unlinked grades show "연결된 관리지침 없음".
//   - Visual distribution bar (segments proportional to each grade's range,
//     color-coded).
//   - Auto-fill hint.
//
// COMPLEX ("복합 조건형") mode was removed per Req Clarification ADM.036
// (SHCT-391): the hospital dropped complex conditions from scope, so only the
// SUM ("합산 점수형") method is offered. The "산출 방식" SUM/COMPLEX selector is
// gone — the SUM editor renders directly. The wire types (`gradeMethod`,
// `gradeComplex`) are kept for BE-contract compatibility; the form always sends
// `gradeMethod: 'SUM'`.

import { useTranslation } from 'react-i18next';
import { BarChart3, Info } from 'lucide-react';

import { NumberInput } from '@/components/forms/NumberInput';
import { useGuidelinesQuery } from '@/features/management-guidelines/hooks/use-guidelines';
import type { ManagementGuideline } from '@/features/management-guidelines/types/management-guidelines.types';
import type { GradeKey } from '../types/pro-item-design.types';
import { GRADE_KEYS } from '../types/pro-item-design.types';
import { useWriteDenyGuard } from '@/shared/hooks/useWriteGuard';

import { GuidelineReadonly } from './GuidelineReadonly';

interface GradeCutoffSectionProps {
  gradeSumG1Max: number;
  gradeSumG2Max: number;
  // SHCT-394 — used to resolve the read-only linked guideline from the guideline
  // side (linkedItemId + grade), which is the source of truth (No 45 Option A /
  // PRD 04 v1.5 §3.3).
  // SHCT-220 — this is the item's ID, not its name: names are not unique across
  // sub-areas, so a name-keyed lookup showed the wrong guideline. The id is also
  // immune to the live-edited form name the old prop had to dodge.
  itemId: number;
  // PRD §8 — keyed by the contract path (e.g. `gradeSum.g1Max`,
  // `gradeGuidelines.g2`). Values are ready-to-render translated messages.
  fieldErrors: Record<string, string>;
  canWrite: boolean;
  onG1MaxChange: (v: number) => void;
  onG2MaxChange: (v: number) => void;
}

// Inline border + CSS-var background lookups so the section is self-
// contained. Matches `.grade-section--g{N}` + `.grade-bar__seg--g{N}` from the
// prototype while staying compatible with the React + inline-styles pattern
// used elsewhere in this feature.
const GRADE_BORDER: Record<GradeKey, string> = {
  g1: '3px solid var(--grade-g1)',
  g2: '3px solid var(--grade-g2)',
  g3: '3px solid var(--grade-g3)',
};

const GRADE_BAR_BG: Record<GradeKey, { background: string; color: string }> = {
  g1: { background: 'var(--badge-success-bg)', color: 'var(--badge-success-text)' },
  g2: { background: 'var(--badge-warning-bg)', color: 'var(--badge-warning-text)' },
  g3: { background: '#FEE2E8', color: 'var(--destructive)' },
};

const GRADE_PILL_CLASS: Record<GradeKey, string> = {
  g1: 'grade grade-g1',
  g2: 'grade grade-g2',
  g3: 'grade grade-g3',
};

export function GradeCutoffSection(props: GradeCutoffSectionProps) {
  const { t } = useTranslation('pro-item-design');
  const guidelinesQuery = useGuidelinesQuery();
  return (
    <>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <BarChart3 size={14} />
        {t('item.fields.gradeCutoff.sectionLabel')}
      </div>

      {/* PRD §3.3 — SUM ("합산 점수형") is the only grade-calculation method.
          The COMPLEX method + its selector were removed per ADM.036 (SHCT-391). */}
      <SumModeEditor {...props} guidelinesQuery={guidelinesQuery} />

      <p
        style={{
          fontSize: 11,
          color: 'var(--muted-foreground)',
          marginTop: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <Info size={12} style={{ flexShrink: 0 }} />
        {t('item.fields.gradeCutoff.guidelineNotice')}
      </p>
    </>
  );
}

type SumModeEditorProps = GradeCutoffSectionProps & {
  guidelinesQuery: ReturnType<typeof useGuidelinesQuery>;
};

function SumModeEditor({
  gradeSumG1Max,
  gradeSumG2Max,
  itemId,
  fieldErrors,
  canWrite,
  onG1MaxChange,
  onG2MaxChange,
  guidelinesQuery,
}: SumModeEditorProps) {
  const { t } = useTranslation('pro-item-design');
  const guidelines = guidelinesQuery.data ?? [];

  const g2Lower = gradeSumG1Max + 1;
  const g3Lower = gradeSumG2Max + 1;
  // PRD §3.3 / §8 — overlap error sits on `gradeSum.g1Max` (the first input
  // the user has agency over). We surface it under G1 + G2 cards so the user
  // can see it next to the offending range.
  const overlapError = fieldErrors['gradeSum.g1Max'];

  return (
    <>
      <GradeCard grade="g1">
        <GradeCardHead
          grade="g1"
          guidelines={guidelines}
          itemId={itemId}
        />
        <SumRange>
          <SumRangeAuto>0</SumRangeAuto>
          <span>~</span>
          <SumRangeInput
            id="grade-sum-g1-max"
            value={gradeSumG1Max}
            onChange={onG1MaxChange}
            canWrite={canWrite}
          />
        </SumRange>
      </GradeCard>

      <GradeCard grade="g2">
        <GradeCardHead
          grade="g2"
          guidelines={guidelines}
          itemId={itemId}
        />
        <SumRange>
          <SumRangeAuto>{g2Lower}</SumRangeAuto>
          <span>~</span>
          <SumRangeInput
            id="grade-sum-g2-max"
            value={gradeSumG2Max}
            onChange={onG2MaxChange}
            canWrite={canWrite}
          />
        </SumRange>
      </GradeCard>

      <GradeCard grade="g3">
        <GradeCardHead
          grade="g3"
          guidelines={guidelines}
          itemId={itemId}
        />
        <SumRange>
          <SumRangeAuto>{g3Lower}</SumRangeAuto>
          <span>~</span>
          <span style={{ color: 'var(--muted-foreground)', fontStyle: 'italic' }}>
            {t('item.fields.gradeCutoff.sum.maxLabel')}
          </span>
        </SumRange>
      </GradeCard>

      {overlapError ? (
        <p className="form-field__error" style={{ marginTop: 4 }}>
          {overlapError}
        </p>
      ) : null}

      <DistributionBar
        g1Max={gradeSumG1Max}
        g2Lower={g2Lower}
        g2Max={gradeSumG2Max}
        g3Lower={g3Lower}
      />

      <p
        style={{
          fontSize: 11,
          color: 'var(--muted-foreground)',
          marginTop: 2,
        }}
      >
        {t('item.fields.gradeCutoff.sum.autoFillHint')}
      </p>
    </>
  );
}

// ── Small subcomponents ────────────────────────────────────────────────────

function GradeCard({
  grade,
  children,
}: {
  grade: GradeKey;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderLeft: GRADE_BORDER[grade],
        borderRadius: 8,
        padding: '14px 16px',
        marginBottom: 10,
      }}
    >
      {children}
    </div>
  );
}

interface GradeCardHeadProps {
  grade: GradeKey;
  guidelines: ManagementGuideline[];
  itemId: number;
}

function GradeCardHead({
  grade,
  guidelines,
  itemId,
}: GradeCardHeadProps) {
  const { t } = useTranslation('pro-item-design');
  // Q75 (PRD §3.3/§8.1 v1.8): guideline linkage is not required to publish, so
  // no guideline-required error is surfaced here — the linkage is read-only.
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className={GRADE_PILL_CLASS[grade]} style={{ flexShrink: 0 }}>
          {t(`item.fields.gradeCutoff.grades.${grade}.label`)}
        </span>
        <span style={{ fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
          {t(`item.fields.gradeCutoff.grades.${grade}.sublabel`)}
        </span>
        {/* #5 — a long linked-guideline name must ellipsis-truncate instead of
            growing the row and breaking the card layout. */}
        <span
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 11,
            color: 'var(--muted-foreground)',
            minWidth: 0,
          }}
        >
          <span style={{ flexShrink: 0 }}>
            {t('item.fields.gradeCutoff.guidelineLabel')}:
          </span>
          <GuidelineReadonly grade={grade} guidelines={guidelines} itemId={itemId} />
        </span>
      </div>
    </div>
  );
}

function SumRange({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 13,
      }}
    >
      {children}
    </div>
  );
}

function SumRangeAuto({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        color: 'var(--muted-foreground)',
        minWidth: 30,
        textAlign: 'center',
        fontWeight: 500,
      }}
    >
      {children}
    </span>
  );
}

function SumRangeInput({
  id,
  value,
  onChange,
  canWrite,
}: {
  id: string;
  value: number;
  onChange: (v: number) => void;
  canWrite: boolean;
}) {
  // SHCT-582 — PRD 21 §7.4 P-2: the field was already `readOnly` without PRO_MGMT:WRITE but said
  // nothing about why.
  const { denyProps } = useWriteDenyGuard(canWrite);
  return (
    // SHCT-607 — NumberInput, not a raw <input type="number">: a raw one left the
    // typed digit rendered as "05" because React's loose-equality DOM sync never
    // rewrote the field. See components/forms/NumberInput.tsx.
    <NumberInput
      id={id}
      min={0}
      value={value}
      onChange={(n) => {
        // Grade bounds are whole, non-negative scores. Empty input coerces to 0 via
        // NumberInput's emptyValue — that saves the user from a stuck field while
        // still letting them type freely; the save handler enforces the strict
        // overlap/missing checks.
        onChange(Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0);
      }}
      readOnly={!canWrite}
      {...denyProps}
      style={{
        width: 72,
        height: 30,
        padding: '0 8px',
        border: '1px solid var(--border)',
        borderRadius: 6,
        fontSize: 13,
        textAlign: 'center',
        background: 'var(--input-background)',
      }}
    />
  );
}

interface DistributionBarProps {
  g1Max: number;
  g2Lower: number;
  g2Max: number;
  g3Lower: number;
}

function DistributionBar({ g1Max, g2Lower, g2Max, g3Lower }: DistributionBarProps) {
  const { t } = useTranslation('pro-item-design');
  // SHCT-581 — the three segments are EQUAL WIDTH, matching the prototype's
  // `.grade-bar__seg{flex:1}` (pro-structure.html). PRD 04 §3.3 asks only for
  // "시각 bar로 구간 분포 실시간 표시", and the prototype's live handler rewrites
  // only the range numbers — it never touches segment width.
  //
  // This previously sized each segment proportionally to its numeric band, with
  // G3 (open-ended, so it has no true proportion) filled in as the average of
  // the other two. Nothing in the PRD, the prototype or the clarification log
  // defined that, and it made the bar skew badly as soon as real cutoffs were
  // entered: g1Max=3 / g2Max=0 rendered 50% / 17% / 33% and clipped the short
  // segment's label, which is the reported defect.
  return (
    <div
      style={{
        display: 'flex',
        gap: 3,
        margin: '12px 0',
        fontSize: 11,
        borderRadius: 6,
        overflow: 'hidden',
      }}
    >
      <BarSeg grade="g1">
        <span className={GRADE_PILL_CLASS.g1} style={{ marginRight: 4 }}>
          {t('item.fields.gradeCutoff.grades.g1.label')}
        </span>
        0 ~ {g1Max}
      </BarSeg>
      <BarSeg grade="g2">
        <span className={GRADE_PILL_CLASS.g2} style={{ marginRight: 4 }}>
          {t('item.fields.gradeCutoff.grades.g2.label')}
        </span>
        {g2Lower} ~ {g2Max}
      </BarSeg>
      <BarSeg grade="g3">
        <span className={GRADE_PILL_CLASS.g3} style={{ marginRight: 4 }}>
          {t('item.fields.gradeCutoff.grades.g3.label')}
        </span>
        {g3Lower} ~
      </BarSeg>
    </div>
  );
}

function BarSeg({
  grade,
  children,
}: {
  grade: GradeKey;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        // Equal thirds — prototype `.grade-bar__seg{flex:1}`. minWidth/overflow
        // stay as a safety net so a narrow detail panel ellipsises the label
        // instead of overflowing the bar.
        flex: 1,
        minWidth: 0,
        padding: '7px 10px',
        textAlign: 'center',
        fontWeight: 500,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        ...GRADE_BAR_BG[grade],
      }}
    >
      {children}
    </div>
  );
}

// Re-export so future slices can reuse the constants without re-deriving.
export { GRADE_KEYS };
